import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./BankInterestRates.css";
import MortgageTools from "../MortgageTools";
import Seo from "../components/Seo";

const loanTypeOptions = [
  { key: "all", label: "All Products", icon: "bi bi-grid" },
  { key: "homeLoan", label: "Home Loan", icon: "bi bi-house-door" },
  {
    key: "loanAgainstProperty",
    label: "Loan Against Property",
    icon: "bi bi-buildings",
  },
  {
    key: "personalLoan",
    label: "Personal Loan",
    icon: "bi bi-person-check",
  },
  {
    key: "balanceTransfer",
    label: "Balance Transfer",
    icon: "bi bi-arrow-repeat",
  },
  {
    key: "businessLoan",
    label: "Business Loan",
    icon: "bi bi-briefcase",
  },
  { key: "carLoan", label: "Car Loan", icon: "bi bi-car-front" },
];

const banks = [
  {
    id: 1,
    name: "State Bank of India",
    shortName: "SBI",
    logo: "/images/Bank Logos/sbibank.png",
    rates: {
      homeLoan: "7.10%",
      loanAgainstProperty: "11.30% p.a.",
      personalLoan: "10.10%",
      balanceTransfer: "7.65%",
      businessLoan: null,
      carLoan: "8.80%",
    },
  },
  {
    id: 2,
    name: "Bank of India",
    shortName: "BOI",
    logo: "/images/Bank Logos/bankofindia.png",
    rates: {
      homeLoan: "7.10%",
      loanAgainstProperty: "9.10% p.a.",
      personalLoan: "11.10%",
      balanceTransfer: "7.35%",
      businessLoan: null,
      carLoan: "7.85%",
    },
  },
  {
    id: 3,
    name: "Bank of Maharashtra",
    shortName: "BOM",
    logo: "/images/Bank Logos/bankofmaharashtra.png",
    rates: {
      homeLoan: "7.10%",
      loanAgainstProperty: "9.95% p.a.",
      personalLoan: "9.00%",
      balanceTransfer: "7.35%",
      businessLoan: null,
      carLoan: "7.70%",
    },
  },
  {
    id: 4,
    name: "Central Bank of India",
    shortName: "CBI",
    logo: "/images/Bank Logos/centralbankofindia.png",
    rates: {
      homeLoan: "7.10%",
      loanAgainstProperty: "9.50% p.a.",
      personalLoan: null,
      balanceTransfer: "7.35%",
      businessLoan: null,
      carLoan: "7.90%",
    },
  },
  {
    id: 5,
    name: "HDFC Bank",
    shortName: "HDFC",
    logo: "/images/Bank Logos/hdfcbank.png",
    rates: {
      homeLoan: "7.10%",
      loanAgainstProperty: "11.80% p.a.",
      personalLoan: "10.90%",
      balanceTransfer: "7.90%",
      businessLoan: "10.70%",
      carLoan: "7.90%",
    },
  },
  {
    id: 6,
    name: "Bank of Baroda",
    shortName: "BOB",
    logo: "/images/Bank Logos/bankofbarodha.png",
    rates: {
      homeLoan: "7.20%",
      loanAgainstProperty: "9.70% p.a.",
      personalLoan: "10.30%",
      balanceTransfer: "7.45%",
      businessLoan: null,
      carLoan: "8.40%",
    },
  },
  {
    id: 7,
    name: "Kotak Mahindra Bank",
    shortName: "Kotak",
    logo: "/images/Bank Logos/kotak_mahindra_bank.png",
    rates: {
      homeLoan: "7.99%",
      loanAgainstProperty: "9.50% p.a.",
      personalLoan: "10.99%",
      balanceTransfer: "7.99%",
      businessLoan: null,
      carLoan: null,
    },
  },
  {
    id: 8,
    name: "Axis Bank",
    shortName: "Axis",
    logo: "/images/Bank Logos/axis_bank.png",
    rates: {
      homeLoan: "7.50%",
      loanAgainstProperty: "9.70%",
      personalLoan: "10.30%",
      balanceTransfer: "8.00%",
      businessLoan: "13.50%",
      carLoan: "7.55%",
    },
  },
  {
    id: 9,
    name: "Bandhan Bank",
    shortName: "Bandhan",
    logo: "/images/Bank Logos/bandhan_bank.png",
    rates: {
      homeLoan: "8.41%",
      loanAgainstProperty: "11.89% p.a.",
      personalLoan: "9.47%",
      balanceTransfer: "8.41%",
      businessLoan: "13.50%",
      carLoan: "12.15%",
    },
  },
  {
    id: 10,
    name: "Indian Overseas Bank",
    shortName: "IOB",
    logo: "/images/Bank Logos/indiaoverseasbank.png",
    rates: {
      homeLoan: "7.10%",
      loanAgainstProperty: "9.00% p.a.",
      personalLoan: "10.35%",
      balanceTransfer: "7.45%",
      businessLoan: null,
      carLoan: "7.85%",
    },
  },
  {
    id: 11,
    name: "IndusInd Bank",
    shortName: "IndusInd",
    logo: "/images/Bank Logos/indusind_bank.png",
    rates: {
      homeLoan: null,
      loanAgainstProperty: "10.08%",
      personalLoan: "10.49%",
      balanceTransfer: null,
      businessLoan: "15.99%",
      carLoan: null,
    },
  },
  {
    id: 12,
    name: "Standard Chartered Bank",
    shortName: "SCB",
    logo: "/images/Bank Logos/standard_chartered_bank.png",
    rates: {
      homeLoan: "8.95%",
      loanAgainstProperty: null,
      personalLoan: "11.99%",
      balanceTransfer: null,
      businessLoan: null,
      carLoan: null,
    },
  },
];

const productLabels = {
  homeLoan: "Home Loan",
  loanAgainstProperty: "Loan Against Property",
  personalLoan: "Personal Loan",
  balanceTransfer: "Balance Transfer",
  businessLoan: "Business Loan",
  carLoan: "Car Loan",
};

const getNumericRate = (rate) => {
  if (!rate) return Number.POSITIVE_INFINITY;

  const parsedRate = parseFloat(rate.replace(/[^\d.]/g, ""));

  return Number.isFinite(parsedRate)
    ? parsedRate
    : Number.POSITIVE_INFINITY;
};

const BankInterestRates = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("all");
  const [sortOrder, setSortOrder] = useState("default");

  const visibleBanks = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    let filteredBanks = banks.filter((bank) => {
      const matchesSearch =
        bank.name.toLowerCase().includes(normalizedSearch) ||
        bank.shortName.toLowerCase().includes(normalizedSearch);

      const matchesProduct =
        selectedProduct === "all" ||
        Boolean(bank.rates[selectedProduct]);

      return matchesSearch && matchesProduct;
    });

    if (sortOrder === "name") {
      filteredBanks = [...filteredBanks].sort((firstBank, secondBank) =>
        firstBank.name.localeCompare(secondBank.name)
      );
    }

    if (
      sortOrder === "lowest" &&
      selectedProduct !== "all"
    ) {
      filteredBanks = [...filteredBanks].sort(
        (firstBank, secondBank) =>
          getNumericRate(firstBank.rates[selectedProduct]) -
          getNumericRate(secondBank.rates[selectedProduct])
      );
    }

    return filteredBanks;
  }, [searchTerm, selectedProduct, sortOrder]);

  const availableRateCount = banks.reduce((total, bank) => {
    return (
      total +
      Object.values(bank.rates).filter(Boolean).length
    );
  }, 0);

  return (
    <>
      <Seo
        title="Bank Loan Interest Rates | MONAYRA (OPC) PRIVATE LIMITED"
        description="Compare indicative interest rates for home loans, Loan Against Property, personal loans, business loans, car loans and balance transfers with MONAYRA."
        keywords="bank loan interest rates, home loan rates, personal loan rates, business loan rates, car loan rates, Loan Against Property rates"
        canonical="https://www.mortbuzz.in/bank-interest-rates"
      />

      <main className="monayra-bank-rates-page">
        <section className="bank-rates-hero">
          <div className="bank-rates-hero-overlay"></div>

          <div
            className="bank-rates-glow bank-rates-glow-left"
            aria-hidden="true"
          ></div>

          <div
            className="bank-rates-glow bank-rates-glow-right"
            aria-hidden="true"
          ></div>

          <div className="bank-rates-page-container bank-rates-hero-grid">
            <div className="bank-rates-hero-content">
              <span className="bank-rates-eyebrow">
                Indicative Lender Comparison
              </span>

              <h1>
                Compare bank loan rates in
                <span> one organised place</span>
              </h1>

              <p>
                Review indicative rates across selected banks for home
                loans, Loan Against Property, personal loans, business
                loans, car loans and balance transfers.
              </p>

              <div className="bank-rates-hero-actions">
                <Link
                  to="/apply-now"
                  className="bank-rates-primary-btn"
                >
                  Apply for a Loan
                  <i className="bi bi-arrow-right"></i>
                </Link>

                <a
                  href="#bank-rate-comparison"
                  className="bank-rates-secondary-btn"
                >
                  <i className="bi bi-bar-chart"></i>
                  Compare Rates
                </a>
              </div>

              <div className="bank-rates-hero-features">
                <div>
                  <i className="bi bi-bank"></i>
                  {banks.length} selected lenders
                </div>

                <div>
                  <i className="bi bi-grid"></i>
                  6 loan categories
                </div>

                <div>
                  <i className="bi bi-search"></i>
                  Search and filter options
                </div>
              </div>
            </div>

            <aside className="bank-rates-summary-card">
              <span className="bank-rates-summary-icon">
                <i className="bi bi-graph-up-arrow"></i>
              </span>

              <span className="bank-rates-card-eyebrow">
                Rate Comparison
              </span>

              <h2>Find a suitable starting point</h2>

              <p>
                Use the filters to compare lenders offering the loan
                product you need.
              </p>

              <div className="bank-rates-summary-stats">
                <div>
                  <strong>{banks.length}</strong>
                  <span>Banks listed</span>
                </div>

                <div>
                  <strong>{availableRateCount}</strong>
                  <span>Rate entries</span>
                </div>
              </div>

              <Link
                to="/emi-calculator"
                className="bank-rates-secondary-btn"
              >
                <i className="bi bi-calculator"></i>
                Calculate EMI
              </Link>
            </aside>
          </div>
        </section>

        <section
          id="bank-rate-comparison"
          className="bank-rates-comparison-section"
        >
          <div className="bank-rates-page-container">
            <div className="bank-rates-section-header">
              <span className="bank-rates-eyebrow">
                Compare Loan Products
              </span>

              <h2>
                Explore indicative rates from
                <span> selected lenders</span>
              </h2>

              <p>
                Search by bank name or select a loan category to narrow
                the comparison.
              </p>
            </div>

            <div className="bank-rates-filter-panel">
              <div className="bank-rates-search-box">
                <i className="bi bi-search"></i>

                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) =>
                    setSearchTerm(event.target.value)
                  }
                  placeholder="Search bank name..."
                  aria-label="Search banks"
                />

                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm("")}
                    aria-label="Clear bank search"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>

              <div className="bank-rates-sort-box">
                <i className="bi bi-sort-down"></i>

                <select
                  value={sortOrder}
                  onChange={(event) =>
                    setSortOrder(event.target.value)
                  }
                  aria-label="Sort bank rates"
                >
                  <option value="default">Default Order</option>
                  <option value="name">Bank Name</option>

                  {selectedProduct !== "all" && (
                    <option value="lowest">
                      Lowest Listed Rate
                    </option>
                  )}
                </select>
              </div>
            </div>

            <div className="bank-rates-product-filters">
              {loanTypeOptions.map((option) => (
                <button
                  type="button"
                  key={option.key}
                  className={
                    selectedProduct === option.key
                      ? "active"
                      : ""
                  }
                  onClick={() => {
                    setSelectedProduct(option.key);

                    if (
                      option.key === "all" &&
                      sortOrder === "lowest"
                    ) {
                      setSortOrder("default");
                    }
                  }}
                >
                  <i className={option.icon}></i>
                  {option.label}
                </button>
              ))}
            </div>

            <div className="bank-rates-results-bar">
              <span>
                Showing <strong>{visibleBanks.length}</strong>{" "}
                {visibleBanks.length === 1 ? "bank" : "banks"}
              </span>

              {selectedProduct !== "all" && (
                <span>
                  Product:{" "}
                  <strong>
                    {productLabels[selectedProduct]}
                  </strong>
                </span>
              )}
            </div>

            {visibleBanks.length > 0 ? (
              <div className="bank-rates-grid">
                {visibleBanks.map((bank) => {
                  const displayedRates =
                    selectedProduct === "all"
                      ? Object.entries(bank.rates)
                      : [
                          [
                            selectedProduct,
                            bank.rates[selectedProduct],
                          ],
                        ];

                  return (
                    <article
                      className="bank-rates-card"
                      key={bank.id}
                    >
                      <div className="bank-rates-card-top">
                        <div className="bank-rates-logo-box">
                          <img
                            src={bank.logo}
                            alt={`${bank.name} logo`}
                            loading="lazy"
                          />
                        </div>

                        <div className="bank-rates-bank-title">
                          <span>{bank.shortName}</span>
                          <h3>{bank.name}</h3>
                        </div>

                        <span className="bank-rates-verified-icon">
                          <i className="bi bi-patch-check-fill"></i>
                        </span>
                      </div>

                      <div className="bank-rates-list">
                        {displayedRates.map(
                          ([productKey, rate]) => (
                            <div
                              className={`bank-rates-row ${
                                rate
                                  ? ""
                                  : "bank-rates-unavailable"
                              }`}
                              key={productKey}
                            >
                              <span>
                                {productLabels[productKey]}
                              </span>

                              <strong>
                                {rate || "Not listed"}
                              </strong>
                            </div>
                          )
                        )}
                      </div>

                      <div className="bank-rates-card-footer">
                        <Link
                          to="/apply-now"
                          state={{
                            selectedBank: bank.name,
                            selectedLoanType:
                              selectedProduct === "all"
                                ? ""
                                : productLabels[selectedProduct],
                          }}
                          className="bank-rates-primary-btn"
                        >
                          Apply Now
                          <i className="bi bi-arrow-right"></i>
                        </Link>

                        <Link
                          to="/emi-calculator"
                          className="bank-rates-calculator-link"
                        >
                          <i className="bi bi-calculator"></i>
                          Calculate EMI
                        </Link>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="bank-rates-empty-state">
                <span>
                  <i className="bi bi-search"></i>
                </span>

                <h3>No matching banks found</h3>

                <p>
                  Try another bank name or select a different loan
                  category.
                </p>

                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedProduct("all");
                    setSortOrder("default");
                  }}
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="bank-rates-guidance-section">
          <div className="bank-rates-page-container">
            <div className="bank-rates-guidance-grid">
              <article>
                <span>
                  <i className="bi bi-percent"></i>
                </span>

                <h3>Rates are profile-dependent</h3>

                <p>
                  Final pricing may depend on credit score, income,
                  employer or business profile, loan amount and lender
                  policy.
                </p>
              </article>

              <article>
                <span>
                  <i className="bi bi-receipt"></i>
                </span>

                <h3>Compare total borrowing cost</h3>

                <p>
                  Consider processing fees, insurance, legal charges,
                  valuation expenses and foreclosure terms.
                </p>
              </article>

              <article>
                <span>
                  <i className="bi bi-calendar3"></i>
                </span>

                <h3>Review rate type and tenure</h3>

                <p>
                  Compare floating or fixed-rate structures and choose a
                  tenure that supports manageable repayment.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className="bank-rates-cta-section">
          <div className="bank-rates-page-container">
            <div className="bank-rates-cta-card">
              <div>
                <span>Need Personalised Assistance?</span>

                <h2>
                  Let MONAYRA help you compare suitable lenders
                </h2>

                <p>
                  Share your loan requirement, income profile and city
                  to receive assistance with eligibility, documents and
                  lender comparison.
                </p>
              </div>

              <div className="bank-rates-cta-actions">
                <Link
                  to="/apply-now"
                  className="bank-rates-primary-btn"
                >
                  Apply Now
                  <i className="bi bi-arrow-right"></i>
                </Link>

                <Link
                  to="/contact"
                  className="bank-rates-secondary-btn"
                >
                  <i className="bi bi-headset"></i>
                  Contact Our Team
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="bank-rates-tools-section">
          <div className="bank-rates-page-container">
            <MortgageTools />
          </div>
        </section>

        <section className="bank-rates-disclaimer">
          <div className="bank-rates-page-container">
            <i className="bi bi-shield-exclamation"></i>

            <p>
              Rates displayed on this page are indicative values from
              the supplied website data and may change without notice.
              Final rate, eligibility, loan amount, tenure, charges and
              approval are determined by the selected lender.
              MONAYRA (OPC) PRIVATE LIMITED provides loan assistance
              and does not guarantee approval.
            </p>
          </div>
        </section>
      </main>
    </>
  );
};

export default BankInterestRates;